import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import axios from "axios";
import { generateText } from "ai";
import { openai } from "@ai-sdk/openai";
import { customAlphabet } from "nanoid";
import Claude from "@/public/assets/icons/Models/Claude.svg";
import ChatGpt from "@/public/assets/icons/Models/ChatGpt.svg";
import Gemini from "@/public/assets/icons/Models/Gemini.svg";
import Groq from "@/public/assets/icons/Models/Groq.svg";

import { startOfDay, endOfDay, subDays } from "date-fns";
import { env } from "@/env/client";

export type TimeRange =
  | "today"
  | "yesterday"
  | "week"
  | "month"
  | "year"
  | "all";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function formatDate(input: string | number | Date): string {
  const date = new Date(input);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export const runAsyncFnWithoutBlocking = (
  fn: (...args: any) => Promise<any>
) => {
  fn();
};

export const formatNumber = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);

export const handleError = (error: unknown) => {
  if (error instanceof Error) {
    console.error(error.message);
    throw new Error(`Error: ${error.message}`);
  } else if (typeof error === "string") {
    console.error(error);
    throw new Error(`Error: ${error}`);
  } else {
    console.error(error);
    throw new Error(`Unknown error: ${JSON.stringify(error)}`);
  }
};

export async function downloadImage(imageUrl: string) {
  try {
    const response = await axios({
      url: imageUrl,
      method: "GET",
      responseType: "arraybuffer",
    });

    const imageBuffer = Buffer.from(response.data, "binary");
    return imageBuffer;
  } catch (error) {
    handleError(error);
    throw error;
  }
}

export function countTokens(prompt: string, modelOutput: string): number {
  const combinedText = `${prompt} ${modelOutput}`;
  const tokens = combinedText.split(/\s+/);
  return tokens.length;
}

export function extractCloudinaryParts(cloudinaryUrl: string) {
  const url = new URL(cloudinaryUrl);
  const path = url.pathname;

  const baseUrl = `${url.protocol}//${url.host}${path.substring(
    0,
    path.indexOf("/v")
  )}`;
  const imagePath = path.substring(path.indexOf("/v"));

  return { baseUrl, imagePath };
}

export async function generateTitleFromPrompt(prompt: string) {
  const { text } = await generateText({
    model: openai(`gpt-4o-mini`),
    prompt: prompt,
    system: `generate small title from prompt in 3 words`,
    maxTokens: 15,
    temperature: 0.7,
  });
  return text;
}

interface Specification {
  key: string;
  value: any;
}

interface SpecificationObject {
  [key: string]: any;
}

export const truncateText = (text: string, maxLength: number) => {
  // console.log(text, "Text");
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
};
export const nanoid = customAlphabet(
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
  7
); // 7-character random string

export function formatDates(date: Date): string {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

export function nextMonth(date: Date): Date {
  return new Date(date.setMonth(date.getMonth() + 1));
}

interface LLM {
  name: string;
  color: string;
  models: string[];
  icon: any;
  supportedTypes: string[];
  maxSize?: number;
  base64Support?: boolean;
  modelswithFile?: string[];
}

export const llms: LLM[] = [
  {
    name: "openAI",
    models: ["gpt-4o", "o1-preview", "o1-mini", "gpt-4o-mini", "gpt-4-turbo"],
    color: "bg-green-500",
    icon: ChatGpt,
    supportedTypes: ["image/png", "image/jpeg", "image/gif", "image/webp"],
    modelswithFile: ["gpt-4o", "gpt-4-turbo", "gpt-4o-mini"],
    maxSize: 20 * 1024 * 1024, // 20MB
    base64Support: true,
  },
  {
    name: "gemini",
    models: [
      "gemini-pro",
      "gemini-2.0-flash-exp",
      "gemini-1.5-pro-latest",
      "gemini-1.5-flash-latest",
      "gemini-1.5-flash",
    ],
    color: "bg-blue-500",
    icon: Gemini,
    supportedTypes: [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/gif",
      "image/webp",
    ],
    modelswithFile: [
      "gemini-1.5-flash",
      "gemini-1.5-pro-latest",
      "gemini-1.5-flash-latest",
      "gemini-2.0-flash-exp",
    ],
    maxSize: 5 * 1024 * 1024, // 5MB
    base64Support: true,
  },
  {
    name: "groq",
    models: [
      "deepseek-r1-distill-llama-70b",
      "llama3-8b-8192",
      "gemma2-9b-it",
      "mixtral-8x7b-32768",
      "llama-3.1-70b-versatile",
      "llama-3.2-3b-preview",
    ],
    color: "bg-purple-500",
    icon: Groq,
    supportedTypes: [],
  },
  {
    name: "anthropic",
    models: [
      "claude-3-opus-20240229",
      "claude-3-5-sonnet-20241022",
      "claude-3-haiku-20240307",
    ],
    color: "bg-yellow-500",
    icon: Claude,
    supportedTypes: ["application/pdf", "image/png", "image/jpeg"],
    modelswithFile: ["claude-3-5-sonnet-20241022"],
    maxSize: 100 * 1024 * 1024, // 100MB
    base64Support: true,
  },
];

export function absoluteUrl(path: string) {
  return `${env.NEXT_PUBLIC_SERVER_URL}${path}`;
}

export function getStartDate(timeRange: TimeRange, now: Date): Date {
  const start = new Date(now);

  switch (timeRange) {
    case "today":
      start.setHours(0, 0, 0, 0);
      break;
    case "yesterday":
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      break;
    case "week":
      start.setDate(start.getDate() - 7);
      break;
    case "month":
      start.setMonth(start.getMonth() - 1);
      break;
    case "year":
      start.setFullYear(start.getFullYear() - 1);
      break;
    case "all":
    default:
      return new Date(0); // Beginning of time
  }

  return start;
}

export function getStartDateUserGeneration(timeRange: TimeRange): Date {
  const now = new Date();
  const start = new Date(now);

  switch (timeRange) {
    case "today":
      start.setHours(0, 0, 0, 0);
      break;
    case "yesterday":
      start.setDate(now.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      break;
    case "week":
      start.setDate(now.getDate() - 7);
      break;
    case "month":
      start.setMonth(now.getMonth() - 1);
      break;
    case "year":
      start.setFullYear(now.getFullYear() - 1);
      break;
    case "all":
    default:
      return new Date(0); // Beginning of time
  }

  return start;
}

export function getDateRange(
  timeRange:
    | "today"
    | "yesterday"
    | "week"
    | "month"
    | "year"
    | "all"
    | "default"
): { startDate: Date; endDate: Date } {
  const now = new Date();
  let startDate: Date;
  let endDate = endOfDay(now);

  switch (timeRange) {
    case "today":
      startDate = startOfDay(now);
      break;
    case "yesterday":
      startDate = startOfDay(subDays(now, 1));
      endDate = endOfDay(subDays(now, 1));
      break;
    case "week":
      startDate = startOfDay(subDays(now, 6));
      break;
    case "month":
      startDate = startOfDay(subDays(now, 29));
      break;
    case "year":
      startDate = startOfDay(subDays(now, 364));
      break;
    case "all":
    default:
      startDate = new Date(0); // Beginning of time
  }

  return { startDate, endDate };
}

// ==================== Image editor general functions ====================

// Helper function to convert image to base64
export const imageToBase64 = async (imageUrl: string): Promise<string> => {
  if (imageUrl.startsWith("data:")) {
    return imageUrl.split(",")[1];
  }

  const response = await fetch(imageUrl);
  const blob = await response.blob();

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      resolve(base64String.split(",")[1]);
    };
    reader.readAsDataURL(blob);
  });
};

export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > shuffled.length / 2; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export async function fetchFeed({ pageParam = 0, offset = 20 }) {
  try {
    const url = new URL(`${env.NEXT_PUBLIC_SERVER_URL}/api/feed`);

    url.searchParams.set("skip", pageParam.toString());
    url.searchParams.set("limit", offset.toString());

    const response = await fetch(url.toString(), {
      method: "GET",
      // 'next' config is recognized in Next.js 13+ (App Router),
      // but for best results, make sure the route is also set
      // to revalidate on the server side.
      next: {
        tags: ["feedPosts"],
        revalidate: 43200, // 12 hours in seconds
      },
    });
    if (!response.ok) {
      throw new Error(
        `Network response was not ok: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching feed:", error);
    throw error;
  }
}

export const feedtools = [
  "text-to-Image",
  "flux",
  "leonardo-ai",
  "gta-style-image",
  "drawing-Generator",
  "text-to-Advance-image",
  "mobile-ui-design",
  "thumbnails",
  "AI-photoshoot",
];

export async function getTools() {
  // Using BaseHub instead of WordPress API
  const { basehub } = await import("@/lib/basehub");

  const data = await basehub.query({
    blog: {
      posts: {
        items: {
          _slug: true,
          _title: true,
          excerpt: true,
        },
      },
    },
  });

  // Transform to match expected format
  return data.blog.posts.items.map((post) => ({
    slug: post._slug,
    title: { rendered: post._title },
    content: { rendered: post.excerpt || "" },
  }));
}

export function getToolLogo(content: string): string {
  const match = content.match(/src="([^"]*)"/);
  return match ? match[1] : "https://via.placeholder.com/40";
}

export function getToolCategory(content: string): string {
  const match = content.match(
    /<p class="text-lg text-blue-600 font-medium category">([^<]*)<\/p>/
  );
  return match ? match[1].trim() : "Unknown Category";
}

export const convertToBase64 = async (file: any): Promise<string> => {
  const buffer = await file.arrayBuffer();
  return Buffer.from(buffer).toString("base64");
};
export function formatTime(duration: number) {
  const minutes = Math.floor(duration / 60000);
  const seconds = Math.floor((duration % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function removeUndefinedProps<T extends object>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as T;
}
